import { Steps } from "antd";
import { Formik } from "formik";
import { useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import ReactQuill from "react-quill-new";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import Breadcrumbs from "../../../components/Breadcrumbs";
import Button from "../../../components/button";
import FileUpload from "../../../components/FileUpload";
import Input from "../../../components/input";
import Select from "../../../components/Select";
import {
  clearCreateTyreState,
  createTyre,
  fetchTyreBrandsDropdown,
  fetchTyreModelsDropdown,
  fetchTyrePatternsDropdown,
  fetchTyreTypesDropdown,
} from "../../../store/Slices/TyreSlice";
import { getWarehousesDropdown } from "../../../store/Slices/PartVendorSlice";
import { fetchVehicleConstructionTypesDropdown } from "../../../store/Slices/VehicleDropdownSlice";
import "../../../styles/Access.css";
import "../../../styles/AssetsRegistration.css";
import "../../../styles/Issue.css";
import "../../../styles/Trip.css";
import {
  findCollectionByKeys,
  formats,
  modules,
  normalizeOptions,
} from "../../../utils/helper";
import { errorNotify, successNotify } from "../../../utils/toast";
import { tyreCurrentStatusOptions, DEFAULT_TYRE_NOTES, getNextTyreSystemId } from "./tyreAssets.constants";

const stripHtml = (value) =>
  String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();

const isPresent = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim() !== "";
  return true;
};

const getValueByPaths = (source, paths = []) => {
  if (!source || !paths.length) return undefined;

  for (const path of paths) {
    const segments = String(path)
      .split(".")
      .map((segment) => segment.trim())
      .filter(Boolean);

    let current = source;
    let validPath = true;

    for (const segment of segments) {
      if (current && typeof current === "object" && segment in current) {
        current = current[segment];
      } else {
        validPath = false;
        break;
      }
    }

    if (validPath && isPresent(current)) {
      return current;
    }
  }

  return undefined;
};

const normalizeTyreStatusValue = (value = "") => {
  const normalizedValue = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

  if (normalizedValue === "installed_on_vehicle") {
    return "installed_vehicle";
  }

  if (normalizedValue === "installed_on_equipment") {
    return "installed_equipment";
  }

  if (normalizedValue === "installed") {
    return "installed";
  }

  return normalizedValue;
};

const resolveApiTyreStatus = (status) => {
  const normalizedStatus = normalizeTyreStatusValue(status);

  if (
    normalizedStatus === "installed_vehicle" ||
    normalizedStatus === "installed_equipment" ||
    normalizedStatus === "installed"
  ) {
    return "installed";
  }

  return normalizedStatus;
};

const fallbackConstructionTypeOptions = [
  { label: "Radial", value: "1" },
  { label: "Bias", value: "2" },
  { label: "Belted Bias", value: "3" },
];

const findOptionByValue = (options = [], value = "") =>
  options.find((option) => String(option?.value) === String(value)) || null;

const mapFiles = (files = []) =>
  (files || [])
    .map((file) => file?.fileName || file?.file_path || file?.path || file)
    .filter(Boolean);

const getOptionLabel = (options = [], value = "", fallback = "N/A") =>
  findOptionByValue(options, value)?.label || fallback;

const toComparableNumber = (value) => {
  const parsed = Number(String(value ?? "").replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
};

const buildCreatePayload = (values) => ({
  serial_number: String(values.serialNumber || "").trim(),
  warehouse_id: Number(values.warehouseId),
  equipment_id: Number(values.equipmentId) || 0,
  brand_id: Number(values.brandId),
  model_id: Number(values.modelId),
  tyre_pattern_id: Number(values.tyrePatternId),
  tyre_type_id: Number(values.tyreTypeId),
  construction_type_id: Number(values.constructionTypeId),
  size: Number(values.tyreSize || values.size),
  tread_depth: Number(values.initialTreadDepth || values.treadDepth),
  current_tread_depth: Number(values.currentTreadDepth) || 0,
  tyre_threshold_values: Number(values.tyreThresholdValues) || 0,
  pressure: Number(values.recommendedPressure || values.pressure),
  load_capacity: Number(values.maximumLoadCapacity || values.loadCapacity),
  changed_at: Number(values.changedAt) || 0,
  last_inspection_date: values.lastInspectionDate || "",
  next_inspection_date: values.nextInspectionDate || "",
  purchase_cost: Number(values.purchaseCost),
  purchase_at: values.purchaseDate || values.purchaseAt,
  status: resolveApiTyreStatus(values.currentStatus || values.status),
  description: values.description || "",
  notes: values.notes || "",
  files: mapFiles(values.files),
});

const buildLocalTyreRecord = ({
  values,
  createdTyreData,
  systemId,
  brandOptions,
  modelOptions,
  patternOptions,
  typeOptions,
  constructionOptions,
  warehouseOptions,
  equipmentOptions,
}) => {
  const files = mapFiles(values.files);
  const resolvedEquipmentId = Number(values.equipmentId) || 0;
  const resolvedStatus = normalizeTyreStatusValue(values.currentStatus || values.status);
  const resolvedTyreSize = String(values.tyreSize || values.size || "").trim();
  const resolvedPurchaseDate = values.purchaseDate || values.purchaseAt || "";
  const resolvedTreadDepth = Number(values.initialTreadDepth || values.treadDepth) || 0;
  const resolvedCurrentTreadDepth = Number(values.currentTreadDepth) || 0;
  const resolvedTyreThresholdValues = Number(values.tyreThresholdValues) || 0;
  const resolvedPressure = Number(values.recommendedPressure || values.pressure) || 0;
  const resolvedLoadCapacity = Number(values.maximumLoadCapacity || values.loadCapacity) || 0;
  const resolvedChangedAt = Number(values.changedAt) || 0;
  const resolvedLastInspectionDate = values.lastInspectionDate || "";
  const resolvedNextInspectionDate = values.nextInspectionDate || "";

  return {
    id: getValueByPaths(createdTyreData, ["id", "tyre_id"]) || `custom-${systemId}`,
    isCustom: true,
    systemId,
    serialNumber: values.serialNumber,
    brand: getOptionLabel(brandOptions, values.brandId, "--"),
    model: getOptionLabel(modelOptions, values.modelId, "--"),
    size: resolvedTyreSize,
    tyrePattern: getOptionLabel(patternOptions, values.tyrePatternId, "--"),
    tyreType: getOptionLabel(typeOptions, values.tyreTypeId, "--"),
    constructionType: getOptionLabel(
      constructionOptions,
      values.constructionTypeId,
      "--"
    ),
    initialTreadDepth: resolvedTreadDepth,
    currentTreadDepth: resolvedCurrentTreadDepth,
    tyreThresholdValues: resolvedTyreThresholdValues,
    recommendedPressure: resolvedPressure,
    maximumLoadCapacity: resolvedLoadCapacity,
    changedAt: resolvedChangedAt,
    lastInspectionDate: resolvedLastInspectionDate,
    nextInspectionDate: resolvedNextInspectionDate,
    purchaseDate: resolvedPurchaseDate,
    purchaseCost: Number(values.purchaseCost) || 0,
    warehouse: getOptionLabel(warehouseOptions, values.warehouseId, "--"),
    currentStatus: resolvedStatus,
    linkedVehicleId: "N/A",
    linkedEquipmentId:
      resolvedEquipmentId > 0
        ? getOptionLabel(equipmentOptions, resolvedEquipmentId, "N/A")
        : "Null",
    currentPosition: resolvedStatus === "in_stock" ? "Null" : "Custom position",
    totalKmRunValue: 0,
    totalKmRun: "0 km",
    lastInstalledDate: resolvedPurchaseDate,
    totalCost: Number(values.purchaseCost) || 0,
    formData: {
      ...values,
      files,
      attachments: files,
      brand: getOptionLabel(brandOptions, values.brandId, "--"),
      model: getOptionLabel(modelOptions, values.modelId, "--"),
      tyrePattern: getOptionLabel(patternOptions, values.tyrePatternId, "--"),
      tyreType: getOptionLabel(typeOptions, values.tyreTypeId, "--"),
      constructionType: getOptionLabel(
        constructionOptions,
        values.constructionTypeId,
        "--"
      ),
      warehouse: getOptionLabel(warehouseOptions, values.warehouseId, "--"),
      size: String(values.tyreSize || values.size || ""),
      tyreSize: resolvedTyreSize,
      initialTreadDepth: resolvedTreadDepth,
      treadDepth: resolvedTreadDepth,
      currentTreadDepth: resolvedCurrentTreadDepth,
      tyreThresholdValues: resolvedTyreThresholdValues,
      recommendedPressure: resolvedPressure,
      pressure: resolvedPressure,
      maximumLoadCapacity: resolvedLoadCapacity,
      loadCapacity: resolvedLoadCapacity,
      changedAt: resolvedChangedAt,
      lastInspectionDate: resolvedLastInspectionDate,
      nextInspectionDate: resolvedNextInspectionDate,
      purchaseDate: resolvedPurchaseDate,
      purchaseAt: resolvedPurchaseDate,
      currentStatus: resolvedStatus,
      status: resolvedStatus === "inactive" ? "inactive" : "active",
    },
    notes: values.notes,
  };
};

const getInitialTyreValues = () => ({
  systemId: getNextTyreSystemId(),
  serialNumber: "",
  warehouseId: "",
  equipmentId: 0,
  brandId: "",
  modelId: "",
  tyrePatternId: "",
  tyreTypeId: "",
  constructionTypeId: "",
  size: "",
  tyreSize: "",
  initialTreadDepth: "",
  currentTreadDepth: "",
  tyreThresholdValues: "",
  recommendedPressure: "",
  maximumLoadCapacity: "",
  changedAt: "",
  lastInspectionDate: "",
  nextInspectionDate: "",
  purchaseCost: "",
  purchaseDate: "",
  purchaseAt: "",
  currentStatus: "in_stock",
  status: "in_stock",
  description: "",
  files: [],
  notes: DEFAULT_TYRE_NOTES,
});

const tyreFormSteps = [
  { title: "General Details" },
  { title: "Attachment" },
];

const tyreStepFields = [
  [
    "serialNumber",
    "warehouseId",
    "brandId",
    "modelId",
    "tyrePatternId",
    "tyreTypeId",
    "constructionTypeId",
    "size",
    "tyreSize",
    "initialTreadDepth",
    "recommendedPressure",
    "maximumLoadCapacity",
    "purchaseCost",
    "purchaseDate",
    "currentStatus",
    "description",
  ],
  ["files"],
];

const tyreStepSchemas = [
  Yup.object().shape({
    serialNumber: Yup.string().required("Serial Number is required"),
    warehouseId: Yup.number()
      .typeError("Warehouse is required")
      .required("Warehouse is required"),
    brandId: Yup.number()
      .typeError("Brand is required")
      .required("Brand is required"),
    modelId: Yup.number()
      .typeError("Model is required")
      .required("Model is required"),
    tyrePatternId: Yup.number()
      .typeError("Tyre Pattern is required")
      .required("Tyre Pattern is required"),
    tyreTypeId: Yup.number()
      .typeError("Tyre Type is required")
      .required("Tyre Type is required"),
    constructionTypeId: Yup.number()
      .typeError("Construction Type is required")
      .required("Construction Type is required"),
    size: Yup.number()
      .typeError("Size must be numeric")
      .positive("Size must be greater than zero")
      .required("Size is required"),
    tyreSize: Yup.number()
      .typeError("Tyre Size must be numeric")
      .positive("Tyre Size must be greater than zero")
      .required("Tyre Size is required"),
    initialTreadDepth: Yup.number()
      .typeError("Initial Tread Depth must be numeric")
      .positive("Tread Depth must be greater than zero")
      .required("Initial Tread Depth is required"),
    recommendedPressure: Yup.number()
      .typeError("Recommended Pressure must be numeric")
      .positive("Pressure must be greater than zero")
      .required("Recommended Pressure is required"),
    maximumLoadCapacity: Yup.number()
      .typeError("Maximum Load Capacity must be numeric")
      .positive("Load Capacity must be greater than zero")
      .required("Maximum Load Capacity is required"),
    purchaseCost: Yup.number()
      .typeError("Purchase Cost must be numeric")
      .positive("Purchase Cost must be greater than zero")
      .required("Purchase Cost is required"),
    purchaseDate: Yup.date().required("Purchase Date is required"),
    currentStatus: Yup.string().required("Current Status is required"),
    description: Yup.string()
      .test(
        "description-max",
        "Description too long",
        (value) => stripHtml(value).length <= 1000
      )
      .nullable(),
  }),
  Yup.object().shape({
    files: Yup.array().nullable(),
  }),
];

const normalizeAssetsRegistrationTab = (value, fallback = "tyres") => {
  const normalized = String(value || "").trim().toLowerCase();
  return ["vehicles", "equipments", "tyres"].includes(normalized)
    ? normalized
    : fallback;
};

const getAssetsRegistrationPath = (tab = "tyres") =>
  `/assets?tab=${normalizeAssetsRegistrationTab(tab)}`;

const getAssetsRegistrationReturnTab = (location, fallback = "tyres") => {
  const params = new URLSearchParams(location?.search || "");
  return normalizeAssetsRegistrationTab(
    params.get("tab") || location?.state?.activeTab || location?.state?.returnTab,
    fallback
  );
};

const CreateTyre = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentFormStep, setCurrentFormStep] = useState(0);
  const returnPath = getAssetsRegistrationPath(
    getAssetsRegistrationReturnTab(location, "tyres")
  );

  const {
    createTyreLoading,
    dropdownError,
    dropdownLoadingCount,
    tyreBrandsDropdownData,
    tyreModelsDropdownData,
    tyrePatternsDropdownData,
    tyreTypesDropdownData,
  } = useSelector((state) => state.tyre);
  const {
    vehicleConstructionTypesDropdownData,
    vehicleDropdownLoadingCount,
    vehicleDropdownError,
  } = useSelector((state) => state.vehicleDropdowns);
  const {
    warehousesDropdownData,
    getWarehousesDropdownLoading,
    getWarehousesDropdownError,
  } = useSelector((state) => state.partVendors);

  useEffect(() => {
    dispatch(clearCreateTyreState());
    dispatch(fetchTyreBrandsDropdown());
    dispatch(fetchTyreModelsDropdown());
    dispatch(fetchTyrePatternsDropdown());
    dispatch(fetchTyreTypesDropdown());
    dispatch(getWarehousesDropdown());
    dispatch(fetchVehicleConstructionTypesDropdown());

    return () => {
      dispatch(clearCreateTyreState());
    };
  }, [dispatch]);

  const dropdownLoading =
    dropdownLoadingCount > 0 ||
    vehicleDropdownLoadingCount > 0 ||
    getWarehousesDropdownLoading;
  const resolvedDropdownError =
    dropdownError || vehicleDropdownError || getWarehousesDropdownError;
  const initialValues = useMemo(() => getInitialTyreValues(), []);

  const brandOptions = useMemo(
    () =>
      normalizeOptions(tyreBrandsDropdownData, {
        valueKeys: ["id", "brand_id"],
        labelKeys: ["label", "name", "brand_name"],
        defaultLabelPrefix: "Brand",
      }),
    [tyreBrandsDropdownData]
  );

  const modelOptions = useMemo(
    () =>
      normalizeOptions(tyreModelsDropdownData, {
        valueKeys: ["id", "model_id"],
        labelKeys: ["label", "name", "model_name"],
        defaultLabelPrefix: "Model",
      }),
    [tyreModelsDropdownData]
  );

  const patternOptions = useMemo(
    () =>
      normalizeOptions(tyrePatternsDropdownData, {
        valueKeys: ["id", "tyre_pattern_id", "pattern_id"],
        labelKeys: ["label", "name", "pattern_name", "tyre_pattern_name"],
        defaultLabelPrefix: "Pattern",
      }),
    [tyrePatternsDropdownData]
  );

  const typeOptions = useMemo(
    () =>
      normalizeOptions(tyreTypesDropdownData, {
        valueKeys: ["id", "tyre_type_id", "type_id"],
        labelKeys: ["label", "name", "type_name", "tyre_type_name"],
        defaultLabelPrefix: "Type",
      }),
    [tyreTypesDropdownData]
  );

  const warehouseOptions = useMemo(
    () =>
      normalizeOptions(warehousesDropdownData, {
        valueKeys: ["value", "id", "warehouse_id"],
        labelKeys: ["label", "warehouse_name", "name"],
        defaultLabelPrefix: "Warehouse",
      }),
    [warehousesDropdownData]
  );

  const constructionOptions = useMemo(() => {
    const source = Array.isArray(vehicleConstructionTypesDropdownData)
      ? vehicleConstructionTypesDropdownData
      : findCollectionByKeys(vehicleConstructionTypesDropdownData, [
          "constructiontypes",
          "constructiontype",
          "constructiontypedropdown",
          "constructiontypelist",
          "vehicleconstructiontypes",
        ]);

    const normalizedConstructionOptions = normalizeOptions(source, {
      valueKeys: ["value", "id", "construction_type_id"],
      labelKeys: ["label", "construction_type_name", "name", "title"],
      defaultLabelPrefix: "Construction Type",
    });

    return normalizedConstructionOptions.length
      ? normalizedConstructionOptions
      : fallbackConstructionTypeOptions;
  }, [vehicleConstructionTypesDropdownData]);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const payload = buildCreatePayload(values);
      await dispatch(createTyre(payload)).unwrap();

      successNotify("Tyre created successfully");
      navigate(returnPath);
    } catch (error) {
      errorNotify(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={tyreStepSchemas[currentFormStep]}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        setFieldValue,
        validateForm,
        handleSubmit,
        setTouched,
      }) => {
        const isLastStep = currentFormStep === tyreFormSteps.length - 1;

        const updateFiles = (updater) => {
          const nextFiles = typeof updater === "function" ? updater(values.files || []) : updater;
          setFieldValue("files", nextFiles);
        };

        const handleNext = async () => {
          const allErrors = await validateForm();
          const activeStepFields = tyreStepFields[currentFormStep] || [];
          const hasCurrentStepErrors = activeStepFields.some((fieldName) =>
            Object.prototype.hasOwnProperty.call(allErrors, fieldName)
          );

          if (hasCurrentStepErrors) {
            const touchedMap = {};
            activeStepFields.forEach((fieldName) => {
              touchedMap[fieldName] = true;
            });
            setTouched(touchedMap);
            return;
          }

          setCurrentFormStep((previous) => previous + 1);
        };

        const handlePrevious = () => {
          setCurrentFormStep((previous) => Math.max(0, previous - 1));
        };

        const handleTopSave = async () => {
          if (!isLastStep) {
            await handleNext();
            return;
          }

          handleSubmit();
        };

        return (
          <div className="create_vehicle_page create_tyre_page">
            <Breadcrumbs values={["Assets", "Tyres", "Tyre Registration"]} />

            <div className="page_header access_view">
              <h2>Tyre Registration</h2>

              <div className="d-flex align-items-center gap-2">
                <Button
                  cancelBtn={() => navigate(returnPath)}
                  submitBtn={handleTopSave}
                  submitText="Save"
                  type="button"
                  shouldRow={false}
                  loading={createTyreLoading}
                  disabled={createTyreLoading}
                />
              </div>
            </div>

            <div className="page_container">
              <Row>
                <Col md={9}>
                  <div className="trip_information_main_div">
                    <h5>Registration Information</h5>

                    {resolvedDropdownError && (
                      <div
                        className="error-message"
                        style={{ color: "#ff0000de", marginBottom: "12px" }}
                      >
                        {resolvedDropdownError}
                      </div>
                    )}

                    <div>
                      <h6>
                        System ID: <span>{values.systemId}</span>
                      </h6>

                      <div className="d-flex trip_create_steps_layout">
                        <div className="trip_create_steps_sidebar">
                          <Steps
                            className="create_vehicle_steps form_steps_vertical"
                            current={currentFormStep}
                            direction="vertical"
                            items={tyreFormSteps}
                            onChange={async (targetStep) => {
                              if (targetStep <= currentFormStep) {
                                setCurrentFormStep(targetStep);
                                return;
                              }

                              const allErrors = await validateForm();
                              const activeStepFields =
                                tyreStepFields[currentFormStep] || [];
                              const hasCurrentStepErrors = activeStepFields.some((fieldName) =>
                                Object.prototype.hasOwnProperty.call(allErrors, fieldName)
                              );

                              if (hasCurrentStepErrors) {
                                const touchedMap = {};
                                activeStepFields.forEach((fieldName) => {
                                  touchedMap[fieldName] = true;
                                });
                                setTouched(touchedMap);
                                return;
                              }

                              setCurrentFormStep(targetStep);
                            }}
                          />
                        </div>

                        <div className="w-100 mb-3">
                          {currentFormStep === 0 && (
                            <Row>
                              <Col md={6}>
                                <Input
                                  label="Serial Number"
                                  placeholder="Enter Serial Number"
                                  name="serialNumber"
                                  value={values.serialNumber}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={errors.serialNumber}
                                  touched={touched.serialNumber}
                                />
                              </Col>
                              <Col md={6}>
                                <Select
                                  label="Brand"
                                  placeholder="Select Brand"
                                  options={brandOptions}
                                  value={findOptionByValue(brandOptions, values.brandId)}
                                  name="brandId"
                                  onChange={(option) =>
                                    setFieldValue("brandId", option ? option.value : "")
                                  }
                                  onBlur={handleBlur}
                                  error={errors.brandId}
                                  touched={touched.brandId}
                                  loading={dropdownLoading}
                                  isSearch={false}
                                />
                              </Col>
                              <Col md={6}>
                                <Input
                                  label="Size"
                                  placeholder="00"
                                  type="number"
                                  name="size"
                                  value={values.size}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={errors.size}
                                  touched={touched.size}
                                  showExtraText
                                  extraText="Inches"
                                />
                              </Col>
                              <Col md={6}>
                                <Select
                                  label="Model"
                                  placeholder="Select Model"
                                  options={modelOptions}
                                  value={findOptionByValue(modelOptions, values.modelId)}
                                  name="modelId"
                                  onChange={(option) =>
                                    setFieldValue("modelId", option ? option.value : "")
                                  }
                                  onBlur={handleBlur}
                                  error={errors.modelId}
                                  touched={touched.modelId}
                                  loading={dropdownLoading}
                                  isSearch={false}
                                />
                              </Col>
                              <Col md={6}>
                                <Select
                                  label="Tyre Pattern"
                                  placeholder="Select Tyre Pattern"
                                  options={patternOptions}
                                  value={findOptionByValue(patternOptions, values.tyrePatternId)}
                                  name="tyrePatternId"
                                  onChange={(option) =>
                                    setFieldValue("tyrePatternId", option ? option.value : "")
                                  }
                                  onBlur={handleBlur}
                                  error={errors.tyrePatternId}
                                  touched={touched.tyrePatternId}
                                  loading={dropdownLoading}
                                  isSearch={false}
                                />
                              </Col>
                              <Col md={6}>
                                <Select
                                  label="Tyre Type"
                                  placeholder="Select Tyre Type"
                                  options={typeOptions}
                                  value={findOptionByValue(typeOptions, values.tyreTypeId)}
                                  name="tyreTypeId"
                                  onChange={(option) =>
                                    setFieldValue("tyreTypeId", option ? option.value : "")
                                  }
                                  onBlur={handleBlur}
                                  error={errors.tyreTypeId}
                                  touched={touched.tyreTypeId}
                                  loading={dropdownLoading}
                                  isSearch={false}
                                />
                              </Col>
                              <Col md={6}>
                                <Input
                                  label="Tyre Size"
                                  placeholder="00"
                                  type="number"
                                  name="tyreSize"
                                  value={values.tyreSize}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={errors.tyreSize}
                                  touched={touched.tyreSize}
                                />
                              </Col>
                              <Col md={6}>
                                <Select
                                  label="Construction Type"
                                  placeholder="Select Construction Type"
                                  options={constructionOptions}
                                  value={findOptionByValue(
                                    constructionOptions,
                                    values.constructionTypeId
                                  )}
                                  name="constructionTypeId"
                                  onChange={(option) =>
                                    setFieldValue(
                                      "constructionTypeId",
                                      option ? option.value : ""
                                    )
                                  }
                                  onBlur={handleBlur}
                                  error={errors.constructionTypeId}
                                  touched={touched.constructionTypeId}
                                  loading={dropdownLoading}
                                  isSearch={false}
                                />
                              </Col>
                              <Col md={6}>
                                <Input
                                  label="Initial Tread Depth"
                                  placeholder="00"
                                  type="number"
                                  name="initialTreadDepth"
                                  value={values.initialTreadDepth}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={errors.initialTreadDepth}
                                  touched={touched.initialTreadDepth}
                                  showExtraText
                                  extraText="mm"
                                />
                              </Col>
                              <Col md={6}>
                                <Input
                                  label="Current Tread Depth"
                                  placeholder="00"
                                  type="number"
                                  name="currentTreadDepth"
                                  value={values.currentTreadDepth}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  showExtraText
                                  extraText="mm"
                                  required={false}
                                />
                              </Col>
                              <Col md={6}>
                                <Input
                                  label="Threshold Values"
                                  placeholder="00"
                                  type="number"
                                  name="tyreThresholdValues"
                                  value={values.tyreThresholdValues}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  showExtraText
                                  extraText="km"
                                  required={false}
                                />
                              </Col>
                              <Col md={6}>
                                <Input
                                  label="Recommended Pressure"
                                  placeholder="00"
                                  type="number"
                                  name="recommendedPressure"
                                  value={values.recommendedPressure}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={errors.recommendedPressure}
                                  touched={touched.recommendedPressure}
                                  showExtraText
                                  extraText="PSI"
                                />
                              </Col>
                              <Col md={6}>
                                <Input
                                  label="Maximum Load Capacity"
                                  placeholder="00"
                                  type="number"
                                  name="maximumLoadCapacity"
                                  value={values.maximumLoadCapacity}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={errors.maximumLoadCapacity}
                                  touched={touched.maximumLoadCapacity}
                                  showExtraText
                                  extraText="kg"
                                />
                              </Col>
                              <Col md={6}>
                                <Input
                                  label="Changed At"
                                  placeholder="00"
                                  type="number"
                                  name="changedAt"
                                  value={values.changedAt}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  showExtraText
                                  extraText="km"
                                  required={false}
                                />
                              </Col>
                              <Col md={6}>
                                <Input
                                  label="Last Inspection Date"
                                  type="date"
                                  name="lastInspectionDate"
                                  value={values.lastInspectionDate}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  required={false}
                                />
                              </Col>
                              <Col md={6}>
                                <Input
                                  label="Next Inspection Date"
                                  type="date"
                                  name="nextInspectionDate"
                                  value={values.nextInspectionDate}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  required={false}
                                />
                              </Col>
                              <Col md={6}>
                                <Input
                                  label="Purchase Date"
                                  type="date"
                                  name="purchaseDate"
                                  value={values.purchaseDate}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={errors.purchaseDate}
                                  touched={touched.purchaseDate}
                                />
                              </Col>
                              <Col md={6}>
                                <Input
                                  label="Purchase Cost"
                                  placeholder="00"
                                  type="number"
                                  name="purchaseCost"
                                  value={values.purchaseCost}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={errors.purchaseCost}
                                  touched={touched.purchaseCost}
                                  showExtraText
                                  extraText="PKR"
                                />
                              </Col>
                              <Col md={6}>
                                <Select
                                  label="Warehouse"
                                  placeholder="Select Warehouse"
                                  options={warehouseOptions}
                                  value={findOptionByValue(warehouseOptions, values.warehouseId)}
                                  name="warehouseId"
                                  onChange={(option) =>
                                    setFieldValue("warehouseId", option ? option.value : "")
                                  }
                                  onBlur={handleBlur}
                                  error={errors.warehouseId}
                                  touched={touched.warehouseId}
                                  loading={dropdownLoading}
                                  isSearch={false}
                                />
                              </Col>
                              <Col md={6}>
                                <Select
                                  label="Current Status"
                                  placeholder="Select Current Status"
                                  options={tyreCurrentStatusOptions}
                                  value={findOptionByValue(tyreCurrentStatusOptions, values.currentStatus)}
                                  name="currentStatus"
                                  onChange={(option) =>
                                    setFieldValue("currentStatus", option ? option.value : "")
                                  }
                                  onBlur={handleBlur}
                                  error={errors.currentStatus}
                                  touched={touched.currentStatus}
                                  isSearch={false}
                                />
                              </Col>
                              <Col md={12}>
                                <Input
                                  label="Description"
                                  placeholder="Write tyre details if any..."
                                  name="description"
                                  value={values.description}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={errors.description}
                                  touched={touched.description}
                                  quill
                                  setFieldValue={setFieldValue}
                                  required={false}
                                  className="create_vehicle_description"
                                />
                              </Col>
                            </Row>
                          )}

                          {currentFormStep === 1 && (
                            <Row>
                              <Col md={12}>
                                <div className="issue_file_upload_wrapper">
                                  <label>Attachment</label>
                                  <FileUpload
                                    destination="tyre-registration"
                                    files={values.files}
                                    setFiles={updateFiles}
                                    maxFileSize={10}
                                    maxFiles={5}
                                    className="issue_file_upload create_vehicle_upload"
                                  />
                                  {touched.files && errors.files && (
                                    <div
                                      className="error-message"
                                      style={{
                                        color: "#ff0000de",
                                        fontSize: "12px",
                                        marginTop: "3px",
                                        marginLeft: "5px",
                                      }}
                                    >
                                      {errors.files}
                                    </div>
                                  )}
                                </div>
                              </Col>
                            </Row>
                          )}

                          <Row>
                            <Col md={12}>
                              <div className="d-flex align-items-center gap-2">
                                <Button
                                  cancelBtn={handlePrevious}
                                  cancelBtnDisabled={currentFormStep === 0 || createTyreLoading}
                                  submitText={isLastStep ? "Save" : "Next"}
                                  type="button"
                                  shouldRow={false}
                                  submitBtn={isLastStep ? handleSubmit : handleNext}
                                  cancelBtnText="Previous"
                                  loading={createTyreLoading}
                                  disabled={createTyreLoading}
                                />
                              </div>
                            </Col>
                          </Row>
                        </div>
                      </div>
                    </div>
                  </div>
                </Col>

                <Col md={3}>
                  <div className="trip_notes_main_div create_vehicle_notes">
                    <h5>Notes</h5>
                    <ReactQuill
                      placeholder="Write notes here..."
                      modules={modules}
                      formats={formats}
                      value={values.notes || ""}
                      onChange={(content) => setFieldValue("notes", content)}
                      className="react_quill_input notes_quill_input quill-small-font trip_quill_input"
                    />
                  </div>
                </Col>
              </Row>
            </div>
          </div>
        );
      }}
    </Formik>
  );
};

export default CreateTyre;
